import { EnvironmentInjector, Injectable, inject } from '@angular/core';
import { fromJson, toJson } from '@bufbuild/protobuf';
import { NbToastrService } from '@nebular/theme';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  CollectionNames,
  JobTypeServiceCreateResponseJson,
  JobTypeServiceCreateResponseSchema,
  JobTypeServiceListRequestJson,
  JobTypeServiceListRequestSchema,
  defaultApiSerializeJsonOptions,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { AuthRunEffects } from '@vmspdb/ui/store';
import { EMPTY, Observable, of, tap } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { JobTypeRepositoryService, withSearchSync } from '../../repositories';
import { UtilitiesFacade } from '../utilities/utilities.facade';
import { PaginatedJobTypeActions } from './paginated-job-type.actions';

@Injectable()
export class PaginatedJobTypeEffects extends AuthRunEffects {
  private readonly injector = inject(EnvironmentInjector);
  private readonly errors = inject(GlobalErrorHandlerService);
  private readonly repository = inject(JobTypeRepositoryService);
  private readonly notifications = inject(NbToastrService);
  private readonly utilities = inject(UtilitiesFacade);

  listPaginatedJobTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        PaginatedJobTypeActions.listPaginatedJobTypes,
        PaginatedJobTypeActions.cancel,
      ),
      switchMap((action) =>
        action.type === PaginatedJobTypeActions.cancel.type
          ? EMPTY
          : of(action).pipe(this.performSearch()),
      ),
    );
  });

  performSearch() {
    return (
      source: Observable<{
        params: JobTypeServiceListRequestJson;
      }>,
    ) =>
      source.pipe(
        debounceTime(250),
        withSearchSync(CollectionNames.JobTypes, this.injector),
        tap(() => this.utilities.queue()),
        switchMap((payload) => {
          return this.repository.list(
            fromJson(JobTypeServiceListRequestSchema, payload.params),
          );
        }),
        tap((data) => {
          if (data.degraded) {
            this.errors.reportSearchDegradation(CollectionNames.JobTypes);
            this.notifications.warning(
              'There was an error with the search server for fetching job types.',
              'Search',
              {
                duration: 5000,
              },
            );
          }
        }),
        map((data) =>
          PaginatedJobTypeActions.listPaginatedJobTypesSuccess({
            data: {
              ...data,
              items: (data.items ?? []).map(
                (item) =>
                  toJson(
                    JobTypeServiceCreateResponseSchema,
                    item,
                    defaultApiSerializeJsonOptions,
                  ) as JobTypeServiceCreateResponseJson,
              ),
            },
          }),
        ),
        catchError((error) => {
          this.errors.reportCaughtError(error);
          return of(
            PaginatedJobTypeActions.listPaginatedJobTypesFailure(error),
          );
        }),
        tap(() => this.utilities.complete()),
      );
  }

  constructor() {
    super(inject(Actions));
  }
}
